import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import { DialogProvider } from 'hooks/use-dialog';
import produce from 'immer';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import MainFilePicker from './main-file-picker';
import PreviewPicker from './preview-picker';
import { MainPreviewContainer, PickerContainer } from './styles';
import CustomFormControl from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';

export interface MultipleFilePickerProps extends BaseElementInputProps {
  type: 'files';
  label?: string;
  valueKey: string;
  previewKey: string;
  disabled?: boolean;
  noErrorText?: boolean;
  maxWidth?: string;
  resizeImage?: boolean;
  resizeWidth?: number;
  resizeHeight?: number;
  required?: boolean;
  fieldType?: 'control' | 'uncontrol';
  fileType: UploadFileTypeEnum;
}

export default function MultipleFilePicker(props: MultipleFilePickerProps) {
  const {
    valueKey,
    previewKey,
    resizeImage,
    resizeWidth,
    resizeHeight,
    maxWidth,
    required,
    fieldType = 'control',
    fileType,
  } = props;
  const { control } = useFormContext<any>();
  const watchValue = useWatch({ control, name: props.name });
  const { field, fieldState } = useController({ name: props.name });
  const context = React.useContext(FormContext);
  const disabled = props.disabled || !context.editable;
  const value = fieldType === 'control' ? field.value : watchValue;

  const handleDeleteFile = (index) => {
    const newFiles = produce(value, (draft) => {
      draft.splice(index, 1);
    });
    field.onChange(newFiles);
  };

  const handleAddOrUpdateFile = (
    files: { filename: string; fileUrl: string }[],
    index?: number,
  ) => {
    const newFiles = produce(value || [], (draft) => {
      files.map((item) => {
        if (index !== undefined) {
          draft[index][valueKey] = item.filename;
          draft[index][previewKey] = item.fileUrl;
        } else {
          draft.push({
            [valueKey]: item.filename,
            [previewKey]: item.fileUrl,
          });
        }
      });
    });
    field.onChange(newFiles);
  };

  const _onPicked = (
    files: { filename: string; fileUrl: string }[],
    idx: number,
  ) => {
    handleAddOrUpdateFile(files, idx);
  };

  return (
    <DialogProvider>
      <CustomFormControl
        required={required}
        label={props.label}
        error={fieldState.error?.message}
        disabled={disabled}
      >
        <PickerContainer $maxWidth={maxWidth}>
          {Array.isArray(value) && (
            <MainPreviewContainer>
              {value.map((item, idx) => {
                return (
                  <>
                    <PreviewPicker
                      disabled={disabled}
                      onDelete={() => handleDeleteFile(idx)}
                      preview={item[props.previewKey]}
                      onPicked={(files) => _onPicked(files, idx)}
                      resizeHeight={resizeHeight}
                      resizeImage={resizeImage}
                      resizeWidth={resizeWidth}
                      isMultiple
                      fileType={fileType}
                    />
                    {/* {idx % 4 !== 3 && <PreviewSeparator />} */}
                  </>
                );
              })}
            </MainPreviewContainer>
          )}
          <MainFilePicker
            disabled={disabled}
            onPicked={(files) => handleAddOrUpdateFile(files)}
            resizeHeight={resizeHeight}
            resizeImage={resizeImage}
            resizeWidth={resizeWidth}
            isMultiple
            fileType={fileType}
          />
        </PickerContainer>
      </CustomFormControl>
    </DialogProvider>
  );
}
